import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faFileInvoice, 
  faEdit, 
  faCheckCircle, 
  faPaperPlane, 
  faMoneyBillWave, 
  faBan 
} from '@fortawesome/free-solid-svg-icons';
import invoicingService from '../../services/invoicingService';
import './InvoiceAuditModal.css';

/**
 * Invoice Audit Modal Component
 * 
 * Displays audit logs for invoice types with filtering capabilities
 * 
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Callback when modal closes
 * @param {string} invoiceType - Type of invoice (outgoing, proforma, incoming, purchase-order)
 * @param {number|null} invoiceId - Specific invoice ID (null for all invoices of type)
 * @param {string|null} invoiceNumber - Invoice number for display
 */
const InvoiceAuditModal = ({ 
  isOpen, 
  onClose, 
  invoiceType, 
  invoiceId = null,
  invoiceNumber = null 
}) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // Filter options based on invoice audit actions
  const filterOptions = [
    { value: 'all', label: 'All Actions', icon: faFileInvoice },
    { value: 'CREATED', label: 'Created', icon: faFileInvoice },
    { value: 'UPDATED', label: 'Updated', icon: faEdit },
    { value: 'STATUS_CHANGED', label: 'Status Changed', icon: faEdit },
    { value: 'APPROVED', label: 'Approved', icon: faCheckCircle },
    { value: 'SENT', label: 'Sent', icon: faPaperPlane },
    { value: 'PAID', label: 'Paid', icon: faMoneyBillWave },
    { value: 'CANCELLED', label: 'Cancelled', icon: faBan },
    { value: 'VOIDED', label: 'Voided', icon: faBan },
  ];

  // Load audit logs when modal opens or filters change
  useEffect(() => {
    if (isOpen) {
      loadAuditLogs();
      // Prevent body scroll when modal is open
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, invoiceType, invoiceId]);

  // Filter logs when active filter changes
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredLogs(auditLogs);
    } else {
      setFilteredLogs(auditLogs.filter(log => log.action === activeFilter));
    }
  }, [activeFilter, auditLogs]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await invoicingService.fetchInvoiceAuditLogs(
        invoiceType, 
        invoiceId,
        {}
      );
      setAuditLogs(data.results || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
      setLoading(false);
    }
  };

  const handleFilterChange = (filterValue) => {
    setActiveFilter(filterValue);
  };

  const formatActionLabel = (action) => {
    const actionLabels = {
      'CREATED': 'Created',
      'UPDATED': 'Updated',
      'STATUS_CHANGED': 'Status Changed',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected',
      'SENT': 'Sent',
      'PAID': 'Marked as Paid',
      'CANCELLED': 'Cancelled',
      'VOIDED': 'Voided',
      'PDF_GENERATED': 'PDF Generated',
    };
    return actionLabels[action] || action;
  };

  const getActionBadgeClass = (action) => {
    const classMap = {
      'CREATED': 'created',
      'UPDATED': 'updated',
      'STATUS_CHANGED': 'status-changed',
      'APPROVED': 'approved',
      'REJECTED': 'rejected',
      'SENT': 'sent',
      'PAID': 'paid',
      'CANCELLED': 'cancelled',
      'VOIDED': 'voided',
      'PDF_GENERATED': 'pdf-generated',
    };
    return classMap[action] || 'default';
  };

  const formatChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return 'N/A';
    }
    
    return Object.entries(changes).map(([field, change]) => {
      if (typeof change === 'object' && change.old !== undefined && change.new !== undefined) {
        return `${field}: ${change.old} → ${change.new}`;
      }
      return `${field}: ${JSON.stringify(change)}`;
    }).join(', ');
  };

  if (!isOpen) return null;

  const modalTitle = invoiceId 
    ? `Audit Log - ${invoiceNumber || 'Invoice'}`
    : `Audit Center - ${invoiceType === 'outgoing' ? 'Outgoing Invoices' : 
                       invoiceType === 'proforma' ? 'Pro-forma Invoices' : 
                       invoiceType === 'incoming' ? 'Incoming Invoices' : 
                       'Purchase Orders'}`;

  return (
    <div className="invoice-audit-modal-overlay" onClick={onClose}>
      <div className="invoice-audit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="invoice-audit-modal-header">
          <h2>{modalTitle}</h2>
          <button 
            className="invoice-audit-close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="invoice-audit-filter-tabs">
          <div className="invoice-audit-tabs-container">
            {filterOptions.map((filter) => (
              <button
                key={filter.value}
                className={`invoice-audit-filter-button ${activeFilter === filter.value ? 'active' : ''}`}
                data-filter={filter.value}
                onClick={() => handleFilterChange(filter.value)}
              >
                <div className="invoice-audit-filter-icon">
                  <FontAwesomeIcon icon={filter.icon} />
                </div>
                <span>{filter.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Audit Logs Content */}
        <div className="invoice-audit-logs-content">
          {loading ? (
            <div className="invoice-audit-loading">
              <div className="spinner"></div>
              <p>Loading audit logs...</p>
            </div>
          ) : filteredLogs && filteredLogs.length > 0 ? (
            <table className="invoice-audit-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Invoice Number</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={log.id}>
                    <td>{new Date(log.timestamp).toLocaleString()}</td>
                    <td>
                      <div className="invoice-audit-user-info">
                        <div className="invoice-audit-user-name">{log.user_name || 'Unknown'}</div>
                      </div>
                    </td>
                    <td>
                      <span className={`invoice-audit-action-badge ${getActionBadgeClass(log.action)}`}>
                        {formatActionLabel(log.action)}
                      </span>
                    </td>
                    <td>
                      <strong>{log.invoice_number}</strong>
                    </td>
                    <td>
                      <div className="invoice-audit-details">
                        {log.notes || formatChanges(log.changes)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="invoice-audit-empty">
              <FontAwesomeIcon icon={faFileInvoice} className="empty-icon" />
              <p>No audit logs found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceAuditModal;

